/**
 * Match Sport — app-side snapshot of the game's own `zporti-match-v1` state blob. The WebView's
 * localStorage is the working copy; this mirror only exists so progress survives WebView storage
 * eviction on native (spec AC-8/AC-9). The blob is stored verbatim (the game owns its format) —
 * only a JSON sanity check guards against persisting garbage.
 *
 * Mirrors `monkeyWorldsStarsStorage.helper.ts`: web uses localStorage, native AsyncStorage, every
 * operation swallows errors (fail open — storage must never block play). Intentionally NOT keyed
 * by userId — playable anonymously, per-device (AC-10).
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { logger } from '../../helpers/logger.helper';

const STORAGE_KEY = 'zporti-match-state';

function sanitize(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return raw;
  } catch {
    return null;
  }
}

export async function readMatchSportState(): Promise<string | null> {
  try {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      return sanitize(localStorage.getItem(STORAGE_KEY));
    }
    return sanitize(await AsyncStorage.getItem(STORAGE_KEY));
  } catch (error) {
    if (__DEV__) logger.warn('matchSportStateStorage: read failed', { error });
    return null;
  }
}

/** Persist the latest state blob from a bridge `save` event — last write wins. */
export async function saveMatchSportState(state: string): Promise<void> {
  if (!sanitize(state)) return;
  try {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, state);
    } else {
      await AsyncStorage.setItem(STORAGE_KEY, state);
    }
  } catch (error) {
    if (__DEV__) logger.warn('matchSportStateStorage: write failed', { error });
  }
}
